import type { AccessCollection, AccessResponse, AccessViewResponse } from '@1webit/cvs-api-scheme';

import type { RightModule } from '@/components/right-wrapper/right-wrapper.config';
import { getRightsViewTag } from '@/components/right-wrapper/right-wrapper.config';
import type { Option } from '@/components/ui/select-react/select.interface';
import type { TPermissions } from '@/types';
import { getPermissionTag, ModuleTag } from '@/types';

export const getMaxEditLevel = (adminLevel: number, isSuperUser: boolean): number => {
  if (isSuperUser) {
    return 9;
  }
  return adminLevel < 5 ? -1 : adminLevel - 1;
};
export const getPermissionOptions = (maxLevel: number): Option[] => {
  return Array.from({ length: maxLevel }).map((_, level) => ({
    label: `Oprávnění ${level + 1}`,
    value: level + 1,
  }));
};

export const getModuleAccess = (authData: AccessCollection, module: ModuleTag): AccessResponse | undefined => {
  return authData.find(m => m.moduleName === module);
};
export const getRightModuleAccess = (authData: AccessCollection, module: RightModule): AccessResponse | undefined => {
  if (module === 'player-transfer') {
    return getModuleAccess(authData, ModuleTag.TRANSFERS);
  }
  return getModuleAccess(authData, module);
};

export const getViewAccess = <T extends ModuleTag>(moduleAuthData: AccessResponse, moduleName: T, view: keyof TPermissions[T]): AccessViewResponse | undefined => {
  const tag = getPermissionTag(moduleName, view);
  return moduleAuthData.accessViewCollection.find(v => v.viewName === tag);
};

/**
 * Checks whether a user has access to a module, or to a specific view within that module.
 *
 * Access to a view is only granted when both the module itself (`authData.access`) and the
 * view's own access flag are true — a view can never grant access the parent module denies.
 *
 * @param authData - The module's access data, as returned for the current user (e.g. from `getModuleAccess`).
 * @param moduleTag - The module to check access for.
 * @param view - Optional view within the module. When omitted, only module-level access is checked.
 * @returns `true` if access is granted, `false` otherwise.
 *
 * @example
 * ```tsx
 * checkAccess(authData, ModuleTag.CLUBS, 'EDIT_LOGO')
 * ```
 */
export const checkAccess = <T extends keyof TPermissions>(
  authData: AccessResponse,
  moduleTag: T,
  view?: keyof TPermissions[T],
): boolean => {
  if (view === undefined) {
    return authData.access;
  }
  const tag = getPermissionTag(moduleTag, view);
  return authData.access && (authData.accessViewCollection.find(v => v.viewName === tag)?.access ?? false);
};

/**
 * Checks whether a user has access to the "rights" (permission management) view of a module,
 * i.e. whether they are allowed to view/manage permissions for that module.
 *
 * @param authData - The module's access data, as returned for the current user (e.g. from `getRightModuleAccess`).
 * @param moduleTag - The right-enabled module to check the rights view for.
 * @returns `true` if the user has access to the module's rights view, `false` otherwise.
 */
export const checkRightsAccess = (authData: AccessResponse, moduleTag: RightModule): boolean => {
  const rightsTag = getRightsViewTag(moduleTag);
  return authData.access && (authData.accessViewCollection.find(v => v.viewName === rightsTag)?.access ?? false);
};
